import Button from '@oberoninternal/travelbase-ds/components/action/Button';
import Body from '@oberoninternal/travelbase-ds/components/primitive/Body';
import Title from '@oberoninternal/travelbase-ds/components/primitive/Title';
import { getFontCss } from '@oberoninternal/travelbase-ds/constants/theme';
import router from 'next/router';
import React, { FC } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { MyOrderFragment } from '../generated/graphql';
import { isDeclined } from '../utils/order';

interface Props {
    orderNumber?: string;
    requiresPayment: boolean;
    createdDate: string;
    orderId: string;
    numBookings: number;
    numTickets: number;
    orderItems: MyOrderFragment['orderItems'];
}

const BookingTile: FC<React.PropsWithChildren<Props>> = ({
    orderNumber,
    createdDate,
    orderId,
    requiresPayment,
    numBookings,
    numTickets,
    orderItems,
}) => {
    const isCancelled = orderItems.length > 0 && isDeclined(orderItems[0]);
    return (
        <BookingTileWrapper>
            {isCancelled && <CancelledTag>Geannuleerd</CancelledTag>}
            <BookingTileContent>
                <div>
                    {orderNumber && (
                        <BookingNumber>
                            <FormattedMessage defaultMessage="Bevestigingsnummer:" />
                            <OrderNumber onClick={() => router.push(`/my/order/${orderId}`)}>{orderNumber}</OrderNumber>
                        </BookingNumber>
                    )}
                    <BookingTitle>
                        <FormattedMessage
                            defaultMessage="Bevestiging {date}"
                            values={{
                                date: <FormattedDate value={createdDate!} format="DAYMONTHYEAR" />,
                            }}
                        />
                    </BookingTitle>
                    <BookingDescriptionWrapper>
                        {numBookings > 0 && (
                            <BookingDescription>
                                <FormattedMessage
                                    defaultMessage="{booking}x Accommodatie"
                                    values={{ booking: numBookings }}
                                />
                            </BookingDescription>
                        )}
                    </BookingDescriptionWrapper>
                    <BookingDescriptionWrapper>
                        {numTickets > 0 && (
                            <BookingDescription>
                                <FormattedMessage
                                    defaultMessage="{booking}x Activiteiten"
                                    values={{ booking: numTickets }}
                                />
                            </BookingDescription>
                        )}
                    </BookingDescriptionWrapper>
                </div>
                {requiresPayment && (
                    <div>
                        <StyledButton onClick={() => router.push(`/my/order/${orderId}/checkout/payment`)}>
                            <FormattedMessage defaultMessage="Nu betalen" />
                        </StyledButton>
                    </div>
                )}
            </BookingTileContent>
        </BookingTileWrapper>
    );
};

export default BookingTile;

const BookingTileWrapper = styled.div`
    background: ${({ theme }) => theme.colors.neutral[0]};
    border: 0.1rem solid rgba(0, 0, 0, 0.17);
    box-shadow: 0 2.4rem 1.4rem rgba(0, 0, 0, 0.01), 0px 11px 11px rgba(0, 0, 0, 0.02), 0px 3px 6px rgba(0, 0, 0, 0.03),
        0px 0px 0px rgba(0, 0, 0, 0.03);
    border-radius: 0.8rem;
    padding: 4rem;
    margin-bottom: 3.2rem;

    @media only screen and (max-width: ${({ theme }) => theme.mediaQueries.s}) {
        padding: 1.6rem;
        margin-bottom: 1.6rem;
    }
`;

const BookingTitle = styled(Title)`
    ${({ theme }) => getFontCss(theme.fontSizes.title.small)};
    color: ${({ theme }) => theme.colors.neutral[90]};
    font-weight: 700;
    margin-bottom: 1.2rem;
`;

const BookingNumber = styled(Body)`
    ${({ theme }) => getFontCss(theme.fontSizes.body.tiny)};
    font-weight: 400;
    margin-bottom: 0.4rem;
    display: flex;
    align-items: center;
`;

const BookingDescriptionWrapper = styled.div`
    display: flex;
`;
const OrderNumber = styled.span`
    margin-left: 0.3rem;
    display: inline-block;
    color: ${({ theme }) => theme.colors.primary[80]};
    font-weight: 600;
    text-decoration: underline;
    text-decoration-thickness: 0.2rem;
    text-underline-offset: 0.4rem;
    cursor: pointer;
`;

const BookingDescription = styled(Body)`
    ${({ theme }) => getFontCss(theme.fontSizes.body.small)};
`;

const BookingTileContent = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;

    @media only screen and (max-width: ${({ theme }) => theme.mediaQueries.s}) {
        display: block;
    }
`;

const StyledButton = styled(Button)`
    @media only screen and (max-width: ${({ theme }) => theme.mediaQueries.s}) {
        width: 100%;
        margin-top: 1.6rem;
    }
`;

const CancelledTag = styled.div`
    display: flex;
    align-items: center;
    padding: 0 0.4rem 0 0.6rem;
    border: 1px solid rgba(62, 113, 169, 0.25);
    border-radius: 0.4rem;
    float: right;
    font-size: ${({ theme }) => getFontCss(theme.fontSizes.body.tiny)};
    background-color: ${({ theme }) => theme.colorAssignments.main};
    margin-right: ${({ theme }) => theme.spacing['20_Tiny']};
`;
